import {
  reactive, ref, readonly, InjectionKey, provide, inject, Ref, onUnmounted, computed,
} from 'vue';

import kebabCase from '@/lib/kebab-case';

interface TabData {
  name : string;
  hash : string;
}

/* eslint-disable no-unused-vars */
const tabsInjectionKey = Symbol('tabs') as InjectionKey<{
  registerTab : (identifier : symbol, tabData : Omit<TabData, 'hash'>) => void;
  deregisterTab : (identifier : symbol) => void;
  activeTab : Readonly<Ref<symbol | undefined>>;
}>;
/* eslint-enable no-unused-vars */

export const useTabs = (group : string = 'activeTab') => {
  const tabs = reactive(new Map<symbol, TabData>());
  const params = new URLSearchParams(window.location.search);

  const registerTab = (identifier : symbol, tabData : Omit<TabData, 'hash'>) => {
    const tab = {
      name: tabData.name,
      hash: kebabCase(tabData.name),
    };
    tabs.set(identifier, tab);
  };

  const deregisterTab = (identifier : symbol) => {
    tabs.delete(identifier);
  };

  const activeTab = ref<symbol>();

  provide(tabsInjectionKey, {
    registerTab,
    deregisterTab,
    activeTab: readonly(activeTab),
  });

  const setActiveTab = (identifier : symbol) => {
    const tab = tabs.get(identifier);
    if (tab) {
      activeTab.value = identifier;
      params.delete(group);
      params.set(group, tab.hash);
      window.history.replaceState({}, '', `${window.location.pathname}?${params}`);
    }
  };

  return {
    tabs: readonly(tabs),
    setActiveTab,
    activeTab,
  };
};

export const useTab = (tabData : Omit<TabData, 'hash'>) => {
  const tabsInjection = inject(tabsInjectionKey);

  if (!tabsInjection) {
    throw new Error('Tabs were not provided');
  }

  const { registerTab, deregisterTab, activeTab } = tabsInjection;

  const tabSymbol = Symbol(tabData.name);

  registerTab(tabSymbol, tabData);

  onUnmounted(() => {
    deregisterTab(tabSymbol);
  });

  const isActive = computed(() => (
    activeTab.value === tabSymbol
  ));

  return {
    isActive,
  };
};
